import { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Header from "../components/Header";
import { api, API, formatApiErrorDetail, getLimitDetail, newClientRequestId, friendlyGenerationError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Heart, Copy, Share2, Play, Pause, BookmarkPlus, BookmarkCheck, Sparkles, RefreshCw, AudioLines } from "lucide-react";

const VOICES = [
  { id: "nova", label: "Warm", note: "close and kind" },
  { id: "echo", label: "Calm", note: "steady and even" },
  { id: "shimmer", label: "Gentle", note: "soft and light" },
  { id: "alloy", label: "Clear", note: "plain and easy" },
  { id: "onyx", label: "Deep", note: "low and grounding" },
  { id: "fable", label: "Tender", note: "lyrical and near" },
];

const TEXTURE_BG =
  "https://static.prod-images.emergentagent.com/jobs/e47687e4-2c30-4839-af89-7a84ff5b6888/images/4cd64fa2551fc201570750d5518f663c7868af380e8d98bd4569ff6fadc6dcb5.png";

export default function PrayerView() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const audioRef = useRef(null);
  const [prayer, setPrayer] = useState(null);
  const [savedId, setSavedId] = useState(null); // server prayer id
  const [favorite, setFavorite] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [loadingTts, setLoadingTts] = useState(false);
  const [regenBusy, setRegenBusy] = useState(false);
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");
  const [limitBlock, setLimitBlock] = useState(null);
  const [voice, setVoice] = useState(() => localStorage.getItem("inlight_voice") || "echo");
  const [voiceOpen, setVoiceOpen] = useState(false);
  const [previewing, setPreviewing] = useState(null);
  const previewRef = useRef(null);

  const previewVoice = (id) => {
    if (previewRef.current) {
      previewRef.current.pause();
      previewRef.current = null;
    }
    if (previewing === id) {
      setPreviewing(null);
      return;
    }
    const el = new Audio(`${process.env.REACT_APP_BACKEND_URL}/api/tts/sample/${id}`);
    el.onended = () => setPreviewing(null);
    el.onerror = () => setPreviewing(null);
    previewRef.current = el;
    setPreviewing(id);
    el.play().catch(() => setPreviewing(null));
  };

  const chooseVoice = (id) => {
    if (previewRef.current) {
      previewRef.current.pause();
      previewRef.current = null;
      setPreviewing(null);
    }
    localStorage.setItem("inlight_voice", id);
    setVoice(id);
    setVoiceOpen(false);
    if (audioUrl) {
      audioRef.current?.pause();
      setAudioUrl(null);
      setPlaying(false);
    }
  };

  useEffect(() => {
    // Load from sessionStorage (after generation) OR from location.state (from library)
    let payload = null;
    if (location.state?.prayer) {
      payload = location.state.prayer;
      if (payload.id) {
        setSavedId(payload.id);
        setFavorite(!!payload.favorite);
      }
    } else {
      const raw = sessionStorage.getItem("inlight_current_prayer");
      if (raw) payload = JSON.parse(raw);
    }
    if (!payload) {
      navigate("/builder");
      return;
    }
    setPrayer(payload);
  }, [location.state, navigate]);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(""), 2000);
  };

  const handleSave = async () => {
    if (!prayer || savedId) return savedId;
    try {
      const { data } = await api.post("/prayers/save", {
        session_id: prayer.session_id,
        title: prayer.title,
        body: prayer.body,
        closing: prayer.closing,
        tradition_notes: prayer.tradition_notes,
        mantra: prayer.mantra,
        repetition: prayer.repetition,
        special_day: prayer.special_day,
        journey: prayer.journey,
        inputs: prayer.inputs,
      });
      setSavedId(data.id);
      showToast("Kept in your library");
      return data.id;
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      return null;
    }
  };

  const handleFavorite = async () => {
    let id = savedId || (prayer && prayer.id);
    if (!id) {
      id = await handleSave();
    }
    if (!id) return;
    try {
      const { data } = await api.post(`/prayers/${id}/favorite`);
      setFavorite(data.favorite);
      showToast(data.favorite ? "Marked as favorite" : "Removed from favorites");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const handleCopy = async () => {
    if (!prayer) return;
    try {
      await navigator.clipboard.writeText(`${prayer.title}\n\n${prayer.body}`);
      showToast("Copied to your clipboard");
    } catch {
      showToast("Could not copy");
    }
  };

  const handleShare = async () => {
    if (!prayer) return;
    const text = `${prayer.title}\n\n${prayer.body}\n\n— Inlight`;
    if (navigator.share) {
      try {
        await navigator.share({ title: prayer.title, text });
      } catch {}
    } else {
      await navigator.clipboard.writeText(text);
      showToast("Prayer copied — paste anywhere");
    }
  };

  const handleRegenerate = async () => {
    if (regenBusy || !prayer?.inputs) return;
    const ok = window.confirm("Regenerating will use one of today's prayers. Continue?");
    if (!ok) return;
    setRegenBusy(true);
    setError("");
    setLimitBlock(null);
    try {
      const { data } = await api.post("/prayer/turn", {
        inputs: prayer.inputs,
        client_request_id: newClientRequestId(),
      });
      if (data.type === "question") {
        // If clarification re-emerges, send the user back to the builder to answer it.
        sessionStorage.removeItem("inlight_current_prayer");
        navigate("/builder");
        return;
      }
      const payload = {
        session_id: data.session_id,
        title: data.title,
        body: data.body,
        closing: data.closing,
        tradition_notes: data.tradition_notes,
        mantra: data.mantra,
        repetition: data.repetition,
        special_day: data.special_day,
        journey: data.journey,
        inputs: prayer.inputs,
      };
      sessionStorage.setItem("inlight_current_prayer", JSON.stringify(payload));
      setPrayer(payload);
      setSavedId(null);
      setFavorite(false);
      setAudioUrl(null);
      setPlaying(false);
      showToast("A new prayer has been composed");
    } catch (e) {
      const limit = getLimitDetail(e);
      if (limit) setLimitBlock(limit);
      else setError(friendlyGenerationError(e));
    } finally {
      setRegenBusy(false);
    }
  };

  const handleTts = async () => {
    if (audioUrl) {
      const a = audioRef.current;
      if (a.paused) {
        a.play();
      } else {
        a.pause();
      }
      return;
    }
    setLoadingTts(true);
    setError("");
    try {
      const token = localStorage.getItem("inlight_token");
      const res = await fetch(`${API}/tts`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ text: `${prayer.title}. ${prayer.body}`, voice }),
      });
      if (!res.ok) throw new Error("Could not generate audio");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      setAudioUrl(url);
      setTimeout(() => audioRef.current?.play(), 50);
    } catch (e) {
      setError(e.message || "Could not generate audio");
    } finally {
      setLoadingTts(false);
    }
  };

  if (!prayer) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "var(--sanctuary-bg)" }}>
        <p data-testid="prayer-loading" className="font-serif-display italic" style={{ color: "var(--sanctuary-ink-muted)" }}>
          Opening the scroll…
        </p>
      </div>
    );
  }

  // Calm full-screen limit overlay (e.g. user hit daily limit while regenerating)
  if (limitBlock) {
    const isGuestSignup = !!limitBlock.signup_required;
    return (
      <div className="min-h-screen" style={{ backgroundColor: "var(--sanctuary-bg)" }}>
        <Header />
        <div className="relative max-w-xl mx-auto px-6 pt-28 md:pt-36 pb-20 text-center fade-up" data-testid="prayer-limit-screen">
          <p className="text-[11px] tracking-[0.35em] uppercase mb-8" style={{ color: "var(--sanctuary-ink-muted)" }}>
            A quiet pause
          </p>
          <div
            aria-hidden="true"
            className="mx-auto mb-10"
            style={{ width: "44px", height: "1px", backgroundColor: "rgba(203,161,83,0.55)" }}
          />
          <h2 className="font-serif-display text-3xl md:text-4xl leading-snug mb-8 tracking-tight" style={{ color: "var(--sanctuary-ink)" }}>
            {limitBlock.message}
          </h2>
          <div className="mt-10 flex flex-col items-center gap-4">
            {isGuestSignup ? (
              <button onClick={() => navigate("/register")} className="px-8 py-4 rounded-full btn-primary">Create a free account</button>
            ) : (
              <button onClick={() => setLimitBlock(null)} className="px-8 py-4 rounded-full" style={{ background: "rgba(255,255,255,0.7)", border: "1px solid #E6E0D2", color: "var(--sanctuary-ink)" }}>
                Stay with this prayer
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: "var(--sanctuary-bg)" }}>
      <Header />

      <div
        className="prayer-texture relative pt-16 md:pt-24 pb-40 px-6 md:px-10"
        style={{
          backgroundImage: `linear-gradient(var(--sanctuary-veil), var(--sanctuary-veil-2)), url(${TEXTURE_BG})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="max-w-3xl mx-auto fade-up">
          <p className="text-xs tracking-[0.25em] uppercase mb-6 text-center" style={{ color: "var(--sanctuary-gold)" }} data-testid="prayer-eyebrow">
            <Sparkles className="inline w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} />
            A prayer for you
          </p>
          <h1
            className="font-serif-display text-3xl sm:text-4xl lg:text-5xl text-center mb-10 leading-tight tracking-tight"
            style={{ color: "var(--sanctuary-ink)" }}
            data-testid="prayer-title"
          >
            {prayer.title}
          </h1>

          <div
            className="font-serif-display text-xl sm:text-2xl lg:text-[1.75rem] leading-loose text-center whitespace-pre-wrap max-w-3xl mx-auto"
            style={{ color: "var(--sanctuary-ink)", fontWeight: 400 }}
            data-testid="prayer-body"
          >
            {prayer.body}
          </div>

          {prayer.mantra && (
            <div className="mt-14 text-center max-w-2xl mx-auto" data-testid="prayer-mantra-block">
              <div
                className="inline-block px-8 py-7 rounded-3xl"
                style={{
                  background: "rgba(255,255,255,0.55)",
                  border: "1px solid rgba(203,161,83,0.35)",
                  boxShadow: "0 8px 32px rgba(45,44,42,0.05)",
                }}
              >
                <p className="text-[10px] tracking-[0.35em] uppercase mb-3" style={{ color: "var(--sanctuary-gold)" }}>
                  Mantra
                </p>
                <p
                  className="font-serif-display text-2xl sm:text-3xl"
                  style={{ color: "var(--sanctuary-ink)", letterSpacing: "0.01em" }}
                  data-testid="prayer-mantra"
                >
                  {prayer.mantra}
                </p>
                {prayer.repetition && (
                  <p
                    className="mt-5 text-sm md:text-base font-serif-display italic leading-relaxed"
                    style={{ color: "var(--sanctuary-ink-soft)" }}
                    data-testid="prayer-repetition"
                  >
                    {prayer.repetition}
                  </p>
                )}
                {prayer.special_day && (
                  <p className="mt-3 text-xs tracking-[0.2em] uppercase" style={{ color: "var(--sanctuary-ink-muted)" }} data-testid="prayer-special-day">
                    Special day · {prayer.special_day}
                  </p>
                )}
                {prayer.journey && (
                  <p
                    className="mt-4 text-sm font-serif-display italic"
                    style={{ color: "var(--sanctuary-ink-soft)" }}
                    data-testid="prayer-journey"
                  >
                    {prayer.journey}
                  </p>
                )}
              </div>
            </div>
          )}

          {prayer.tradition_notes && (
            <p
              className="mt-16 text-sm text-center italic font-serif-display"
              style={{ color: "var(--sanctuary-ink-muted)" }}
              data-testid="prayer-tradition-notes"
            >
              {prayer.tradition_notes}
            </p>
          )}

          {/* Compose another / Regenerate / Sign up for guests */}
          <div className="mt-16 flex flex-col items-center gap-3" data-testid="prayer-foot-actions">
            {prayer.inputs && user && user !== false && (
              <button
                onClick={handleRegenerate}
                disabled={regenBusy}
                data-testid="prayer-regenerate-button"
                className="inline-flex items-center gap-2 px-7 py-3 rounded-full text-sm transition disabled:opacity-50"
                style={{
                  color: "var(--sanctuary-ink)",
                  background: "rgba(255,255,255,0.7)",
                  border: "1px solid #E6E0D2",
                }}
              >
                <RefreshCw className={`w-4 h-4 ${regenBusy ? "animate-spin" : ""}`} strokeWidth={1.5} />
                {regenBusy ? "Composing…" : "Compose a new prayer with the same intention"}
              </button>
            )}
            {user && user !== false ? (
              <button
                onClick={() => navigate("/builder")}
                data-testid="prayer-new-button"
                className="text-sm underline"
                style={{ color: "var(--sanctuary-ink-soft)" }}
              >
                Begin a different prayer
              </button>
            ) : (
              <div
                className="mt-2 w-full max-w-md text-center rounded-3xl px-8 py-8"
                style={{ background: "rgba(255,255,255,0.72)", border: "1px solid #E6E0D2" }}
                data-testid="prayer-guest-invite"
              >
                <p className="font-serif-display text-lg md:text-xl leading-snug mb-6" style={{ color: "var(--sanctuary-ink)" }}>
                  Create a free account to save this prayer and continue your daily practice.
                </p>
                <div className="flex flex-col items-center gap-3">
                  <button
                    onClick={() => navigate("/register")}
                    data-testid="prayer-guest-signup"
                    className="px-8 py-3.5 rounded-full btn-primary text-base"
                  >
                    Save &amp; continue
                  </button>
                  <button
                    onClick={() => navigate("/login")}
                    data-testid="prayer-guest-signin"
                    className="text-sm underline"
                    style={{ color: "var(--sanctuary-ink-soft)" }}
                  >
                    Sign in
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Floating action player */}
      <div className="fixed bottom-6 inset-x-0 z-40 flex flex-col items-center gap-3 pointer-events-none px-4">
        {voiceOpen && user && user !== false && (
          <div
            className="glass-card pointer-events-auto rounded-2xl shadow-lg p-2 w-[260px] fade-up"
            data-testid="voice-picker"
          >
            <p className="text-[11px] tracking-[0.25em] uppercase px-3 pt-1.5 pb-2" style={{ color: "var(--sanctuary-ink-muted)" }}>
              The voice that reads it
            </p>
            {VOICES.map((v) => (
              <div
                key={v.id}
                className="w-full flex items-center gap-1 rounded-xl transition hover-paper"
                style={{ backgroundColor: voice === v.id ? "var(--sanctuary-paper)" : "transparent" }}
              >
                <button
                  onClick={() => previewVoice(v.id)}
                  data-testid={`voice-preview-${v.id}`}
                  aria-label={`Hear the ${v.label} voice`}
                  title={`Hear the ${v.label} voice`}
                  className="ml-1.5 w-7 h-7 rounded-full flex items-center justify-center shrink-0 transition-opacity hover:opacity-70"
                  style={{ color: "var(--sanctuary-accent)" }}
                >
                  {previewing === v.id
                    ? <Pause className="w-3.5 h-3.5" strokeWidth={1.75} />
                    : <Play className="w-3.5 h-3.5" strokeWidth={1.75} />}
                </button>
                <button
                  onClick={() => chooseVoice(v.id)}
                  data-testid={`voice-option-${v.id}`}
                  className="flex-1 flex items-baseline justify-between gap-3 pr-3 py-2 rounded-xl text-left"
                >
                  <span className="font-serif-display text-base" style={{ color: "var(--sanctuary-ink)" }}>
                    {v.label}
                  </span>
                  <span className="text-xs italic" style={{ color: "var(--sanctuary-ink-muted)" }}>
                    {v.note}
                  </span>
                </button>
              </div>
            ))}
            <p className="text-[11px] italic px-3 pt-1 pb-1.5" style={{ color: "var(--sanctuary-ink-muted)" }}>
              Tap ▷ to hear a line before you choose.
            </p>
          </div>
        )}
        <div
          className="glass-card pointer-events-auto rounded-full shadow-lg px-3 py-2 flex items-center gap-1"
          data-testid="prayer-actions"
        >
          <button
            onClick={handleTts}
            disabled={loadingTts}
            data-testid="prayer-tts-button"
            className="w-11 h-11 rounded-full flex items-center justify-center transition disabled:opacity-50"
            style={{ backgroundColor: "var(--sanctuary-accent)", color: "white", display: user && user !== false ? undefined : "none" }}
            title={playing ? "Pause" : "Read aloud"}
          >
            {loadingTts ? (
              <Sparkles className="w-4 h-4 animate-pulse" strokeWidth={1.5} />
            ) : playing ? (
              <Pause className="w-4 h-4" strokeWidth={1.5} />
            ) : (
              <Play className="w-4 h-4 ml-0.5" strokeWidth={1.5} />
            )}
          </button>

          <button
            onClick={() => setVoiceOpen((o) => !o)}
            data-testid="prayer-voice-button"
            className="h-11 px-3 rounded-full inline-flex items-center gap-1.5 transition hover-paper"
            title="Choose the voice"
            style={{ display: user && user !== false ? undefined : "none", color: "var(--sanctuary-ink)" }}
          >
            <AudioLines className="w-4 h-4" strokeWidth={1.5} />
            <span className="text-sm" data-testid="prayer-voice-label">
              {VOICES.find((v) => v.id === voice)?.label || "Calm"}
            </span>
          </button>

          <button
            onClick={handleFavorite}
            data-testid="prayer-favorite-button"
            className="w-11 h-11 rounded-full flex items-center justify-center transition hover-paper"
            title="Favorite"
            style={{ display: user && user !== false ? undefined : "none" }}
          >
            <Heart
              className="w-4 h-4"
              strokeWidth={1.5}
              fill={favorite ? "var(--sanctuary-accent)" : "none"}
              color={favorite ? "var(--sanctuary-accent)" : "var(--sanctuary-ink)"}
            />
          </button>

          <button
            onClick={handleSave}
            disabled={!!savedId}
            data-testid="prayer-save-button"
            className="w-11 h-11 rounded-full flex items-center justify-center transition hover-paper disabled:opacity-50"
            title="Save"
            style={{ display: user && user !== false ? undefined : "none" }}
          >
            {savedId ? (
              <BookmarkCheck className="w-4 h-4" strokeWidth={1.5} color="var(--sanctuary-sage)" />
            ) : (
              <BookmarkPlus className="w-4 h-4" strokeWidth={1.5} color="var(--sanctuary-ink)" />
            )}
          </button>

          <button
            onClick={handleCopy}
            data-testid="prayer-copy-button"
            className="w-11 h-11 rounded-full flex items-center justify-center transition hover-paper"
            title="Copy"
          >
            <Copy className="w-4 h-4" strokeWidth={1.5} color="var(--sanctuary-ink)" />
          </button>

          <button
            onClick={handleShare}
            data-testid="prayer-share-button"
            className="w-11 h-11 rounded-full flex items-center justify-center transition hover-paper"
            title="Share"
          >
            <Share2 className="w-4 h-4" strokeWidth={1.5} color="var(--sanctuary-ink)" />
          </button>
        </div>
      </div>

      {audioUrl && (
        <audio
          ref={audioRef}
          src={audioUrl}
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onEnded={() => setPlaying(false)}
        />
      )}

      {toast && (
        <div
          data-testid="prayer-toast"
          className="fixed bottom-24 inset-x-0 flex justify-center pointer-events-none z-50"
        >
          <div
            className="px-5 py-2.5 rounded-full text-sm fade-in"
            style={{ backgroundColor: "var(--sanctuary-ink)", color: "var(--sanctuary-bg)" }}
          >
            {toast}
          </div>
        </div>
      )}
      {error && (
        <div className="fixed bottom-24 inset-x-0 flex justify-center pointer-events-none z-50">
          <div className="px-5 py-2.5 rounded-full text-sm" style={{ backgroundColor: "var(--sanctuary-alert)", color: "white" }}>
            {error}
          </div>
        </div>
      )}
    </div>
  );
}
